/**
 * Turg'un rejim: ish nuqtasi H_pump(Q) = H_system(Q).
 *
 * TIZIM NAPORI (Bernulli, idish sathlari orasida):
 *   H_sys(Q) = Δz + (P_d − P_s)/(ρg) + Σ_seg [h_f + h_m](Q) + h_valve(Q)
 *   Δz = z_d − z_s
 * ISH NUQTASI: F(Q) = H_pump(Q) − H_sys(Q) = 0; F qat'iy kamayuvchi (nasos pasayuvchi, tizim o'suvchi)
 *   → bitta ildiz. Bracket [0, Q_hi] topilib Newton (newtonScalar, bounds), zaxira bisection.
 *
 * OGOHLANTIRISH KODLARI
 *   pump.noFlow        critical  yopiq zulfin napori ≤ statik napor (yoki nasos o'chiq) → Q = 0
 *   pump.valveClosed   critical  ventil yopiq (opening ≤ 0) → Q = 0
 *   pump.cavitation    critical  NPSH_a < NPSH_r
 *   pump.offBep        info      |Q/Q_bep − 1| > 0.3
 *   pipe.highVelocity  info      v > 3 m/s (bosim quvurida; Kasatkin tavsiya 1…3 m/s)
 *   pipe.laminar       info      Re ≤ 2300 (bosim quvurida)
 *
 * ASSUMPTION: hydraulics.ts va pump.ts dagilar; qo'shimcha:
 *   1. Nasos o'chiq (r = 0) yoki H_shutoff < H_static bo'lsa teskari oqim yo'q (teskari klapan).
 */
import { liquidDensity, saturationPressure, viscosity } from '../../properties';
import { bisection } from '../../solvers/bisection';
import { newtonScalar } from '../../solvers/newton';
import { warn, type SolverDiagnostics, type SteadyResult, type Warning } from '../../types';
import { G, pipeArea, segmentLoss, validateSegment, valveK, type SegmentLoss } from './hydraulics';
import {
  npshRequired,
  pumpEfficiency,
  pumpHead,
  pumpMaxFlow,
  resolveCurve,
  validatePump,
} from './pump';
import type {
  PumpCurve,
  PumpPipelineInputs,
  PumpPipelineOutputs,
  PumpPipelineParams,
} from './types';

export const P_ATM = 101325;

export function validateParams(p: PumpPipelineParams): void {
  validatePump(p.pump);
  validateSegment('discharge', p.discharge);
  if (p.suction) validateSegment('suction', p.suction);
  if (!(p.valve.K0 >= 0)) throw new Error('pump-pipeline: valve.K0 must be ≥ 0');
  if (p.valve.rangeability !== undefined && !(p.valve.rangeability > 1))
    throw new Error('pump-pipeline: valve.rangeability must be > 1');
  if (!Number.isFinite(p.zSuction) || !Number.isFinite(p.zDischarge))
    throw new Error('pump-pipeline: zSuction, zDischarge must be finite');
  if (p.suctionPressure !== undefined && !(p.suctionPressure > 0))
    throw new Error('pump-pipeline: suctionPressure must be > 0');
  if (p.dischargePressure !== undefined && !(p.dischargePressure > 0))
    throw new Error('pump-pipeline: dischargePressure must be > 0');
}

export function validateInputs(i: PumpPipelineInputs): void {
  if (!(i.T > 0)) throw new Error('pump-pipeline: T must be > 0 K');
  if (!(i.opening <= 1)) throw new Error('pump-pipeline: opening must be ≤ 1');
  if (!(i.speedRatio >= 0)) throw new Error('pump-pipeline: speedRatio must be ≥ 0');
}

/** Suyuqlik xossalari va statik napor — bir marta hisoblanadi */
export interface SystemContext {
  rho: number;
  mu: number;
  Pvap: number;
  hStatic: number;
  Kv: number;
  curve: PumpCurve;
  r: number;
}

export function systemContext(
  p: PumpPipelineParams,
  i: PumpPipelineInputs,
  warnings?: Warning[],
): SystemContext {
  const rho = liquidDensity(i.fluid, i.T, warnings);
  const mu = viscosity(i.fluid, 'liquid', i.T, warnings);
  const Pvap = saturationPressure(i.fluid, i.T, warnings);
  const Ps = p.suctionPressure ?? P_ATM;
  const Pd = p.dischargePressure ?? P_ATM;
  const hStatic = p.zDischarge - p.zSuction + (Pd - Ps) / (rho * G);
  return {
    rho,
    mu,
    Pvap,
    hStatic,
    Kv: valveK(p.valve, i.opening),
    curve: resolveCurve(p.pump),
    r: i.speedRatio,
  };
}

export interface SystemLosses {
  discharge: SegmentLoss;
  suction: SegmentLoss | null;
  hFriction: number;
  hMinor: number;
  hValve: number;
  /** Tizim napori (statik + yo'qotishlar), m */
  hSystem: number;
}

/** Tizim napori va tarkibiy qismlari (ishorali, Q < 0 uchun ham) */
export function systemLosses(p: PumpPipelineParams, ctx: SystemContext, Q: number): SystemLosses {
  const discharge = segmentLoss(p.discharge, Q, ctx.rho, ctx.mu);
  const suction = p.suction ? segmentLoss(p.suction, Q, ctx.rho, ctx.mu) : null;
  const v = discharge.velocity;
  const hValve = Number.isFinite(ctx.Kv) ? (ctx.Kv * v * Math.abs(v)) / (2 * G) : 0;
  const hFriction = discharge.hFriction + (suction?.hFriction ?? 0);
  const hMinor = discharge.hMinor + (suction?.hMinor ?? 0);
  return {
    discharge,
    suction,
    hFriction,
    hMinor,
    hValve,
    hSystem: ctx.hStatic + hFriction + hMinor + hValve,
  };
}

/** NPSH_a = (P_s − P_vap)/(ρg) + z_s − (so'rish yo'qotishlari) */
export function npshAvailable(
  p: PumpPipelineParams,
  ctx: SystemContext,
  losses: SystemLosses,
): number {
  const Ps = p.suctionPressure ?? P_ATM;
  const hs = losses.suction ? losses.suction.hFriction + losses.suction.hMinor : 0;
  return (Ps - ctx.Pvap) / (ctx.rho * G) + p.zSuction - Math.abs(hs);
}

/** Barcha chiqishlar berilgan Q da */
export function outputsAt(
  p: PumpPipelineParams,
  ctx: SystemContext,
  Q: number,
  warnings: Warning[],
): PumpPipelineOutputs {
  const losses = systemLosses(p, ctx, Q);
  const H = pumpHead(ctx.curve, Q, ctx.r);
  const eta = pumpEfficiency(p.pump, Q, ctx.r);
  const Ph = ctx.rho * G * Q * H;
  const NPSHa = npshAvailable(p, ctx, losses);
  const NPSHr = npshRequired(p.pump, Q, ctx.r);
  const bepFraction = ctx.r > 0 ? Q / (ctx.r * p.pump.Qbep) : 0;
  const d = losses.discharge;

  if (Q > 0) {
    if (NPSHa < NPSHr)
      warn(warnings, 'pump.cavitation', 'critical', {
        NPSHa: Number(NPSHa.toFixed(2)),
        NPSHr: Number(NPSHr.toFixed(2)),
      });
    if (Math.abs(bepFraction - 1) > 0.3)
      warn(warnings, 'pump.offBep', 'info', { bepFraction: Number(bepFraction.toFixed(2)) });
    if (d.velocity > 3)
      warn(warnings, 'pipe.highVelocity', 'info', { v: Number(d.velocity.toFixed(2)) });
    if (d.regime === 'laminar') warn(warnings, 'pipe.laminar', 'info', { Re: Math.round(d.Re) });
  }

  return {
    Q,
    H,
    dP: ctx.rho * G * H,
    velocity: d.velocity,
    Re: d.Re,
    f: d.f,
    regime: d.regime,
    hFriction: losses.hFriction,
    hMinor: losses.hMinor,
    hValve: losses.hValve,
    hStatic: ctx.hStatic,
    efficiency: eta,
    hydraulicPower: Ph,
    shaftPower: Ph / eta,
    NPSHa,
    NPSHr,
    bepFraction,
    shutoffHead: pumpHead(ctx.curve, 0, ctx.r),
    Kvalve: ctx.Kv,
    rho: ctx.rho,
    mu: ctx.mu,
  };
}

/**
 * Sarfning yuqori chegarasi (F(Q_hi) ≤ 0 kafolatlanadi): nasos egri chizig'i noli
 * (a yoki b > 0) yoki, yassi xarakteristikada, v = 1 m/s dan ikkilantirish.
 */
export function flowUpperBound(p: PumpPipelineParams, ctx: SystemContext): number {
  const qMax = pumpMaxFlow(ctx.curve, ctx.r);
  if (Number.isFinite(qMax)) return qMax;
  const F = (Q: number) => pumpHead(ctx.curve, Q, ctx.r) - systemLosses(p, ctx, Q).hSystem;
  let hi = pipeArea(p.discharge.diameter);
  for (let k = 0; k < 60 && F(hi) > 0; k++) hi *= 2;
  return hi;
}

/** Ish nuqtasi: F(Q) = H_pump − H_sys = 0 */
export function operatingPoint(
  p: PumpPipelineParams,
  ctx: SystemContext,
  warnings: Warning[],
): { Q: number; diagnostics: SolverDiagnostics } {
  const ok = (Q: number, it = 0, res = 0): { Q: number; diagnostics: SolverDiagnostics } => ({
    Q,
    diagnostics: { converged: true, iterations: it, residual: res },
  });
  if (!Number.isFinite(ctx.Kv)) {
    warn(warnings, 'pump.valveClosed', 'critical');
    return ok(0);
  }
  const F = (Q: number) => pumpHead(ctx.curve, Q, ctx.r) - systemLosses(p, ctx, Q).hSystem;
  if (!(ctx.r > 0) || F(0) <= 0) {
    warn(warnings, 'pump.noFlow', 'critical', {
      shutoffHead: Number(pumpHead(ctx.curve, 0, ctx.r).toFixed(2)),
      hStatic: Number(ctx.hStatic.toFixed(2)),
    });
    return ok(0);
  }
  const hi = flowUpperBound(p, ctx);
  const nr = newtonScalar(F, 0.5 * hi, { bounds: [0, hi], xtol: 1e-12, ftol: 1e-10, maxIter: 60 });
  if (nr.converged) return ok(nr.value, nr.iterations, nr.residual);
  const br = bisection(F, 0, hi, { xtol: 1e-13, ftol: 1e-10, maxIter: 200 });
  if (br.converged) return ok(br.value, nr.iterations + br.iterations, br.residual);
  return {
    Q: Number.isFinite(br.value) ? br.value : nr.value,
    diagnostics: {
      converged: false,
      iterations: nr.iterations + br.iterations,
      residual: br.residual,
      message: br.message ?? 'solver.maxIterations',
    },
  };
}

export function solveSteady(
  p: PumpPipelineParams,
  i: PumpPipelineInputs,
): SteadyResult<PumpPipelineOutputs> {
  validateParams(p);
  validateInputs(i);
  const warnings: Warning[] = [];
  const ctx = systemContext(p, i, warnings);
  const { Q, diagnostics } = operatingPoint(p, ctx, warnings);
  const outputs = outputsAt(p, ctx, Q, warnings);
  return { outputs, warnings, diagnostics };
}
